import { Alert, Anchor, Group, Stack, Text } from '@mantine/core';
import { PiUsers } from 'react-icons/pi';
import { Link } from 'wouter';
import {
  DocumentCard,
  FactRow,
} from '@/components/InvoiceDetailsModal/DocumentCard';
import type { TDocumentParties } from '@/models/invoices';
/**
 * Who the document is for, stated once as a small table: the payer (linked), its details for
 * reference (type, address, country, document or registration and VAT numbers), the account and
 * the contact. The printed BILL TO stays in the document; the
 * Monday opportunity is the offer's, on the offer line.
 */
export function PartiesCard({
  parties,
  warnings,
}: {
  parties: TDocumentParties | null | undefined;
  warnings?: string[];
}) {
  const payer = parties?.payer;
  const details = parties?.details;
  return (
    <DocumentCard title="Parties" icon={<PiUsers size={14} />}>
      <Stack gap={6}>
        <FactRow label="Payer">
          {payer ? (
            <>
              <Anchor
                component={Link}
                to={`~/payers/${payer.id}`}
                fz="xs"
                fw={600}
              >
                {payer.name}
              </Anchor>
              {payer.deleted && (
                <Text fz={11} c="red">
                  This payer was deleted.
                </Text>
              )}
              {parties?.printedName &&
                parties.printedName.trim().toLowerCase() !==
                  payer.name.trim().toLowerCase() && (
                  <Text fz={11} c="dimmed">
                    Printed as {parties.printedName}
                  </Text>
                )}
            </>
          ) : (
            <Group gap={6} wrap="wrap">
              <Text fz="xs" c="dimmed">
                {parties?.printedName ?? 'No payer attached'}
              </Text>
            </Group>
          )}
        </FactRow>
        {details && (
          <FactRow label="Details">
            <Stack gap={0}>
              {details?.clientType && <Text fz="xs">{details.clientType}</Text>}
              {details?.address && <Text fz="xs">{details.address}</Text>}
              {details?.country && <Text fz="xs">{details.country}</Text>}
              {details?.ids.map((x) => (
                <Text key={x.label} fz="xs">
                  <Text span inherit c="dimmed">
                    {x.label}
                  </Text>{' '}
                  {x.value}
                </Text>
              ))}
            </Stack>
          </FactRow>
        )}{' '}
        <FactRow label="Account">
          {parties?.account ? (
            <Anchor
              component={Link}
              to={`~/accounts/${parties.account.id}`}
              fz="xs"
            >
              {parties.account.name}
            </Anchor>
          ) : (
            '-'
          )}
        </FactRow>
        <FactRow label="Contact">
          {parties?.contact ? (
            <>
              <Anchor
                component={Link}
                to={`~/contacts/${parties.contact.id}`}
                fz="xs"
              >
                {parties.contact.name || parties.contact.email}
              </Anchor>
              {parties.contact.email && parties.contact.name && (
                <Text fz={11} c="dimmed">
                  {parties.contact.email}
                </Text>
              )}
            </>
          ) : (
            '-'
          )}
        </FactRow>
        {(warnings ?? []).map((w) => (
          <Alert key={w} color="orange" variant="light" p="xs">
            <Text fz="xs">{w}</Text>
          </Alert>
        ))}
      </Stack>
    </DocumentCard>
  );
}
